import { useEffect, useState, useCallback, useRef } from "react";
import API from "../../services/api";
import {
  Row,
  Col,
  Spin,
  Select,
  Button,
  Empty,
  Space,
  Pagination,
  Modal,
  AutoComplete,
} from "antd";
import {
  FilterOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  SearchOutlined,
  BankOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./Businesses.css";
import { filterCities, getLatLngForFilter } from "../locationService";
import {
  validateLocality,
  validateYear,
  validateCustomText,
  isEmpty,
} from "../../utils/Filtervalidation.js";
import { useFilterValidation } from "../../hooks/Usefiltervalidation.js";
// ─────────────────────────────────────────────────────────────────────────────
// NEW: reusable location hook
// ─────────────────────────────────────────────────────────────────────────────
import { useLocationSearch } from "../../hooks/Uselocationsearch.js";

// ─────────────────────────────────────────────────────────────────────────────
// ALLOWED VALUES
// ─────────────────────────────────────────────────────────────────────────────
const ALLOWED_INDUSTRY = [
  "food", "retail", "education", "healthcare", "technology",
  "fitness", "beauty", "real_estate", "other",
];
const ALLOWED_BUSINESS_TYPES = [
  "startup", "private", "public", "agency", "partnership", "other",
];
const ALLOWED_COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "500+"];
const ALLOWED_SORT_BY = ["newest", "oldest"];
const CURRENT_YEAR = new Date().getFullYear();

// ─────────────────────────────────────────────────────────────────────────────
// ValidationTooltip
// ─────────────────────────────────────────────────────────────────────────────
function ValidationTooltip({ message }) {
  if (!message) return null;
  return (
    <div className="vt-tooltip" role="alert" aria-live="polite">
      <span className="vt-tooltip-icon" aria-hidden="true" />
      <span className="vt-tooltip-text">{message}</span>
    </div>
  );
}

function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  // ── Search-bar suggestion state (driven by useLocationSearch hook)
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  // ── Filter-modal locality suggestion state (separate, unchanged)
  const [filterSuggestions, setFilterSuggestions] = useState([]);

  const [customIndustryFilter, setCustomIndustryFilter] = useState("");
  const [customBusinessTypeFilter, setCustomBusinessTypeFilter] = useState("");

  const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);
  const [businessTypeDropdownOpen, setBusinessTypeDropdownOpen] = useState(false);
  const [companySizeDropdownOpen, setCompanySizeDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // ── Validation hook ────────────────────────────────────────────────────────
  const { fieldErrors, setFieldErrors, clearAllFieldErrors, scrollToFirstError } =
    useFilterValidation();

  // ── Refs for scroll-to-error ───────────────────────────────────────────────
  const localityRef = useRef(null);
  const foundedYearRef = useRef(null);
  const customIndustryRef = useRef(null);
  const customBusinessTypeRef = useRef(null);

  const [modalWidth, setModalWidth] = useState(600);

  // filterDebounceRef is still used by the filter-modal locality field
  const filterDebounceRef = useRef(null);

  const filterContentRef = useRef(null);

  const searchDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 576) {
        setModalWidth("96vw");
      } else if (window.innerWidth <= 768) {
        setModalWidth("95vw");
      } else {
        setModalWidth(600);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setIsAdmin(user?.role === "admin");
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    locality: "",
    latitude: null,
    longitude: null,
    type: "",
    industry: [],
    companySize: "",
    minYear: "",
    maxYear: "",
    sortBy: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // ── NEW: reusable location-search hook for the main search bar ─────────────
  const {
    inputValue: searchInputValue,
    isLocationValid: isSearchLocationValid,
    handleChange: handleSearchChange,
    handleSelect: handleSearchSelect,
    handleKeyDown: makeSearchKeyDown,
    handleFocus: handleSearchFocus,
    resetLocation: resetSearchLocation,
  } = useLocationSearch({
    onSuggestionsChange: setSearchSuggestions,
    debounceMs: 300,
    minChars: 2,

    // SYNC FILTER → SEARCH
    syncedLocality: filters.locality,

    // SYNC SEARCH → FILTER
    onSyncLocality: (value) => {

      setFilters((prev) => {

        if (prev.locality === value) {
          return prev;
        }

        return {
          ...prev,
          locality: value,
        };
      });
    },
  });

  // ── Sanitize numeric input ────────────────────────────────────────────────
  const sanitizeYear = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const stripped = String(value).replace(/[^0-9]/g, "");
    if (stripped === "") return "";
    const n = Number(stripped);
    if (!isFinite(n)) return "";
    return stripped;
  };

  // ── Build clean params ────────────────────────────────────────────────────
  const buildParams = useCallback(
    (customFilters, page, pageSize) => {
      const params = { page, limit: pageSize };

      if (customFilters.search?.trim()) {
        params.search = customFilters.search.trim().slice(0, 200);
      }
      if (customFilters.locality?.trim()) {
        params.locality = customFilters.locality.trim().slice(0, 100);
      }
      if (customFilters.type && customFilters.type !== "other") {
        params.type = String(customFilters.type).slice(0, 100);
      } else if (customFilters.type && !ALLOWED_BUSINESS_TYPES.includes(customFilters.type)) {
        params.type = String(customFilters.type).slice(0, 100);
      } else if (customFilters.type) {
        params.type = customFilters.type;
      }

      if (Array.isArray(customFilters.industry) && customFilters.industry.length) {
        params.industry = customFilters.industry.join(",");
      }
      if (customFilters.companySize && ALLOWED_COMPANY_SIZES.includes(customFilters.companySize)) {
        params.companySize = customFilters.companySize;
      }

      const minYear = Number(customFilters.minYear);
      const maxYear = Number(customFilters.maxYear);
      if (
        customFilters.minYear !== "" &&
        isFinite(minYear) &&
        minYear >= 1800 &&
        minYear <= CURRENT_YEAR
      ) {
        params.minYear = minYear;
      }
      if (
        customFilters.maxYear !== "" &&
        isFinite(maxYear) &&
        maxYear >= 1800 &&
        maxYear <= CURRENT_YEAR
      ) {
        params.maxYear = maxYear;
      }
      if (customFilters.sortBy && ALLOWED_SORT_BY.includes(customFilters.sortBy)) {
        params.sortBy = customFilters.sortBy;
      }

      return params;
    },
    []
  );

  const fetchBusinesses = useCallback(
    async (customFilters = filters, page = 1, pageSize = 15) => {
      try {
        setLoading(true);
        const params = buildParams(customFilters, page, pageSize);
        const res = await API.get("/businesses", { params });
        setBusinesses(res.data.businesses || []);
        setPagination({
          current: res.data.currentPage || 1,
          pageSize,
          total: res.data.total || 0,
        });
      } catch (err) {
        console.log("Fetch businesses error:", err);
        setBusinesses([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchBusinesses(filters, 1, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Run per-field validation, collect errors, scroll to first ────────────
  const runFieldValidations = (filtersToValidate, customInd, customBizType) => {
    const errors = {};

    const localityResult = validateLocality(filtersToValidate.locality);
    if (!localityResult.valid) errors.locality = localityResult.message;

    const minYearResult = validateYear(filtersToValidate.minYear, "Min Founded Year");
    if (!minYearResult.valid) {
      errors.foundedYear = minYearResult.message;
    } else {
      const maxYearResult = validateYear(filtersToValidate.maxYear, "Max Founded Year");
      if (!maxYearResult.valid) {
        errors.foundedYear = maxYearResult.message;
      } else if (
        !isEmpty(filtersToValidate.minYear) &&
        !isEmpty(filtersToValidate.maxYear) &&
        Number(filtersToValidate.minYear) > Number(filtersToValidate.maxYear)
      ) {
        errors.foundedYear = "Min Founded Year cannot be greater than Max Founded Year.";
      }
    }

    if (filtersToValidate.industry?.includes("other")) {
      const customIndResult = validateCustomText(customInd, "Custom Industry");
      if (!customIndResult.valid) errors.customIndustry = customIndResult.message;
    }

    if (filtersToValidate.type === "other") {
      const customBizResult = validateCustomText(customBizType, "Custom Business Type");
      if (!customBizResult.valid) errors.customBusinessType = customBizResult.message;
    }

    setFieldErrors(errors);

    const refMap = {
      locality: localityRef,
      foundedYear: foundedYearRef,
      customIndustry: customIndustryRef,
      customBusinessType: customBusinessTypeRef,
    };
    scrollToFirstError(refMap, errors);

    return Object.keys(errors).length === 0;
  };

  // ── REAL‑TIME ERROR CLEARING ─────────────────────────────────────────────
  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;

    const newErrors = { ...fieldErrors };

    // Locality
    if (newErrors.locality) {
      const localityResult = validateLocality(filters.locality);
      if (localityResult.valid) delete newErrors.locality;
    }

    // Founded year
    if (newErrors.foundedYear) {
      const minValid = validateYear(filters.minYear, "Min Founded Year").valid;
      const maxValid = validateYear(filters.maxYear, "Max Founded Year").valid;
      let rangeValid = true;
      if (
        !isEmpty(filters.minYear) &&
        !isEmpty(filters.maxYear) &&
        Number(filters.minYear) > Number(filters.maxYear)
      ) {
        rangeValid = false;
      }
      if (minValid && maxValid && rangeValid) delete newErrors.foundedYear;
    }

    // Custom Industry
    if (newErrors.customIndustry) {
      const customIndResult = validateCustomText(customIndustryFilter, "Custom Industry");
      if (customIndResult.valid) delete newErrors.customIndustry;
    }

    // Custom Business Type
    if (newErrors.customBusinessType) {
      const customBizResult = validateCustomText(customBusinessTypeFilter, "Custom Business Type");
      if (customBizResult.valid) delete newErrors.customBusinessType;
    }

    if (Object.keys(newErrors).length !== Object.keys(fieldErrors).length) {
      setFieldErrors(newErrors);
    }
  }, [
    filters.locality,
    filters.minYear,
    filters.maxYear,
    customIndustryFilter,
    customBusinessTypeFilter,
    fieldErrors,
    setFieldErrors,
  ]);

  useEffect(() => {
    const handleOutsideClick = (e) => {

      // SEARCH DROPDOWN
      const clickedSearchArea = e.target.closest(
        ".bx-search-field"
      );

      if (!clickedSearchArea) {
        setSearchSuggestions([]);
      }

      // FILTER LOCALITY DROPDOWN
      const clickedFilterArea = e.target.closest(
        ".bx-filter-group"
      );

      if (!clickedFilterArea) {
        setFilterSuggestions([]);
      }

      // CUSTOM DROPDOWNS
      if (!e.target.closest(".bx-sort-wrap")) {

        setIndustryDropdownOpen(false);

        setBusinessTypeDropdownOpen(false);

        setCompanySizeDropdownOpen(false);

        setSortDropdownOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    if (filterModalVisible) {
      setTimeout(() => {
        const modalBody = document.querySelector(
          ".bx-filter-modal .ant-modal-body"
        );

        modalBody?.scrollTo({
          top: 0,
          behavior: "auto",
        });
      }, 50);
    }
  }, [filterModalVisible]);

  // ── handleApplyFilters (filter modal) ────────────────────────────────────
  const handleApplyFilters = async () => {
    const isValid = runFieldValidations(filters, customIndustryFilter, customBusinessTypeFilter);
    if (!isValid) return;

    let updatedFilters = {
      ...filters,
      search: "",
      locality: filters.locality?.trim() || "",
    };

    const finalIndustries = [...updatedFilters.industry];
    const otherIndex = finalIndustries.indexOf("other");
    if (otherIndex !== -1) {
      if (customIndustryFilter.trim()) {
        finalIndustries[otherIndex] = customIndustryFilter.trim().slice(0, 100);
      } else {
        finalIndustries.splice(otherIndex, 1);
      }
      updatedFilters.industry = finalIndustries;
    }

    if (updatedFilters.type === "other") {
      updatedFilters.type = customBusinessTypeFilter.trim()
        ? customBusinessTypeFilter.trim().slice(0, 100)
        : "";
    }

    if (!updatedFilters.locality) {
      updatedFilters.latitude = null;
      updatedFilters.longitude = null;
    }

    setFilters(updatedFilters);
    setSearchSuggestions([]);
    clearAllFieldErrors();
    await fetchBusinesses(updatedFilters, 1, pagination.pageSize);
    setFilterModalVisible(false);
  };

  // ── handleSearchBarApply (main search bar) ────────────────────────────────
  // Called ONLY when the Search button is clicked or Enter pressed (valid).
  const handleSearchBarApply = async () => {

    const selectedValue = searchInputValue.trim();

    const updatedFilters = {
      search: selectedValue,
      locality: "",
      latitude: null,
      longitude: null,
      type: "",
      industry: [],
      companySize: "",
      minYear: "",
      maxYear: "",
      sortBy: "",
    };

    setFilters(updatedFilters);

    setSearchSuggestions([]);

    clearAllFieldErrors();

    await fetchBusinesses(
      updatedFilters,
      1,
      pagination.pageSize
    );
  };

  const handleReset = () => {
    const resetFilters = {
      search: "",
      locality: "",
      latitude: null,
      longitude: null,
      type: "",
      industry: [],
      companySize: "",
      minYear: "",
      maxYear: "",
      sortBy: "",
    };
    setFilters(resetFilters);
    setCustomIndustryFilter("");
    setCustomBusinessTypeFilter("");
    resetSearchLocation();
    clearAllFieldErrors();
    fetchBusinesses(resetFilters, 1, pagination.pageSize);
    const modalBody = document.querySelector(
      ".bx-filter-modal .ant-modal-body"
    );

    modalBody?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handlePageChange = (page) => {
    fetchBusinesses(filters, page, 15);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeFiltersCount = [
    filters.locality,
    filters.type === "other" ? customBusinessTypeFilter : filters.type,
    filters.industry?.length > 0 &&
      filters.industry.some((i) => i !== "other" || customIndustryFilter)
      ? "industry"
      : null,
    filters.companySize,
    filters.minYear,
    filters.maxYear,
    filters.sortBy,
  ].filter(Boolean).length;

  const filterDrawer = (
    <div className="bx-filter-panel">
      <Space orientation="vertical" size={14} style={{ width: "100%" }}>

        {/* Locality */}
        <div className="bx-filter-group vt-field-wrap" ref={localityRef}>
          <label className="bx-filter-label">Locality</label>
          <div
            className="bx-filter-search-custom"
            ref={filterDropdownRef}
          >
            <input
              className={`bx-filter-input${fieldErrors.locality ? " vt-input-error" : ""
                }`}
              placeholder="Search locality"
              maxLength={100}
              value={filters.locality}
              onChange={(e) => {
                const value = e.target.value;

                setFilters((prev) => ({
                  ...prev,
                  locality: value,
                  latitude: null,
                  longitude: null,
                }));

                clearTimeout(filterDebounceRef.current);

                if (!value || value.length < 2) {
                  setFilterSuggestions([]);
                  return;
                }

                filterDebounceRef.current = setTimeout(async () => {
                  try {
                    const results = await filterCities(value);
                    setFilterSuggestions(results || []);
                  } catch (err) {
                    console.error(err);
                  }
                }, 500);
              }}
            />

            {filterSuggestions.length > 0 && (
              <div className="bx-filter-dropdown-custom">
                {filterSuggestions.map((item, index) => (
                  <div
                    key={index}
                    className="bx-filter-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        locality: item.value,
                        latitude: null,
                        longitude: null,
                      }));

                      setFilterSuggestions([]);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <ValidationTooltip message={fieldErrors.locality} />
        </div>

        {/* Industry (multi-select with "other") */}
        <div className="bx-filter-group">
          <label className="bx-filter-label">Industry</label>
          <div className="bx-sort-wrap">
            <button
              type="button"
              className={`bx-sort-btn ${industryDropdownOpen ? "active" : ""}`}
              onClick={() => {
                setIndustryDropdownOpen(!industryDropdownOpen);

                setBusinessTypeDropdownOpen(false);
                setCompanySizeDropdownOpen(false);
                setSortDropdownOpen(false);
              }}
            >
              <span>
                {filters.industry?.length
                  ? `${filters.industry.length} selected`
                  : "Select industries"}
              </span>

              <span className={`bx-sort-arrow ${industryDropdownOpen ? "open" : ""}`}>
                ▼
              </span>
            </button>

            {industryDropdownOpen && (
              <div className="bx-sort-dropdown">
                {[
                  { value: "food", label: "Food" },
                  { value: "retail", label: "Retail" },
                  { value: "education", label: "Education" },
                  { value: "healthcare", label: "Healthcare" },
                  { value: "technology", label: "Technology" },
                  { value: "fitness", label: "Fitness" },
                  { value: "beauty", label: "Beauty" },
                  { value: "real_estate", label: "Real Estate" },
                  { value: "other", label: "Other" },
                ].map((item) => {
                  const isSelected = filters.industry.includes(item.value);

                  return (
                    <div
                      key={item.value}
                      className={`bx-sort-item ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        let updated = [...filters.industry];

                        if (updated.includes(item.value)) {
                          updated = updated.filter((v) => v !== item.value);
                        } else {
                          updated.push(item.value);
                        }

                        setFilters((prev) => ({
                          ...prev,
                          industry: updated,
                        }));
                      }}
                    >
                      <span>{item.label}</span>

                      {isSelected && <span className="bx-sort-check">✓</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Custom industry input */}
        {filters.industry && filters.industry.includes("other") && (
          <div
            className="bx-filter-group vt-field-wrap"
            style={{ marginTop: 8 }}
            ref={customIndustryRef}
          >
            <label className="bx-filter-label">Custom Industry</label>
            <input
              type="text"
              placeholder="e.g. Manufacturing, Hospitality, Banking"
              value={customIndustryFilter}
              maxLength={100}
              onChange={(e) => setCustomIndustryFilter(e.target.value)}
              className={`bx-filter-input${fieldErrors.customIndustry ? " vt-input-error" : ""}`}
            />
            <ValidationTooltip message={fieldErrors.customIndustry} />
          </div>
        )}

        {/* Business Type */}
        <div className="bx-filter-group">
          <label className="bx-filter-label">Business Type</label>
          <div className="bx-sort-wrap">
            <button
              type="button"
              className={`bx-sort-btn ${businessTypeDropdownOpen ? "active" : ""}`}
              onClick={() => {
                setBusinessTypeDropdownOpen(!businessTypeDropdownOpen);

                setIndustryDropdownOpen(false);
                setCompanySizeDropdownOpen(false);
                setSortDropdownOpen(false);
              }}
            >
              <span>
                {filters.type
                  ? filters.type.charAt(0).toUpperCase() + filters.type.slice(1)
                  : "Select business type"}
              </span>

              <span className={`bx-sort-arrow ${businessTypeDropdownOpen ? "open" : ""}`}>
                ▼
              </span>
            </button>

            {businessTypeDropdownOpen && (
              <div className="bx-sort-dropdown">
                {[
                  { value: "startup", label: "Startup" },
                  { value: "private", label: "Private" },
                  { value: "public", label: "Public" },
                  { value: "agency", label: "Agency" },
                  { value: "partnership", label: "Partnership" },
                  { value: "other", label: "Other" },
                ].map((item) => (
                  <div
                    key={item.value}
                    className={`bx-sort-item ${filters.type === item.value ? "selected" : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        type: item.value,
                      }));

                      setBusinessTypeDropdownOpen(false);

                      if (item.value !== "other") {
                        setCustomBusinessTypeFilter("");
                      }
                    }}
                  >
                    <span>{item.label}</span>

                    {filters.type === item.value && (
                      <span className="bx-sort-check">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Custom business type input */}
        {filters.type === "other" && (
          <div
            className="bx-filter-group vt-field-wrap"
            style={{ marginTop: 8 }}
            ref={customBusinessTypeRef}
          >
            <label className="bx-filter-label">Custom Business Type</label>
            <input
              type="text"
              placeholder="e.g. LLP, Trust, Franchise"
              value={customBusinessTypeFilter}
              maxLength={100}
              onChange={(e) => setCustomBusinessTypeFilter(e.target.value)}
              className={`bx-filter-input${fieldErrors.customBusinessType ? " vt-input-error" : ""}`}
            />
            <ValidationTooltip message={fieldErrors.customBusinessType} />
          </div>
        )}

        {/* Company Size */}
        <div className="bx-filter-group">
          <label className="bx-filter-label">Company Size</label>
          <div className="bx-sort-wrap">
            <button
              type="button"
              className={`bx-sort-btn ${companySizeDropdownOpen ? "active" : ""}`}
              onClick={() => {
                setCompanySizeDropdownOpen(!companySizeDropdownOpen);

                setIndustryDropdownOpen(false);
                setBusinessTypeDropdownOpen(false);
                setSortDropdownOpen(false);
              }}
            >
              <span>
                {filters.companySize || "Select company size"}
              </span>

              <span className={`bx-sort-arrow ${companySizeDropdownOpen ? "open" : ""}`}>
                ▼
              </span>
            </button>

            {companySizeDropdownOpen && (
              <div className="bx-sort-dropdown">
                {[
                  "1-10",
                  "11-50",
                  "51-200",
                  "201-500",
                  "500+",
                ].map((item) => (
                  <div
                    key={item}
                    className={`bx-sort-item ${filters.companySize === item ? "selected" : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        companySize: item,
                      }));

                      setCompanySizeDropdownOpen(false);
                    }}
                  >
                    <span>{item} employees</span>

                    {filters.companySize === item && (
                      <span className="bx-sort-check">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Founded Year Range */}
        <div className="bx-filter-group vt-field-wrap" ref={foundedYearRef}>
          <label className="bx-filter-label">Founded Year</label>
          <div className="bx-filter-row">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Min"
              min={1800}
              max={CURRENT_YEAR}
              value={filters.minYear}
              className={`bx-filter-input bx-filter-input-half${fieldErrors.foundedYear ? " vt-input-error" : ""}`}
              onChange={(e) => {
                const val = sanitizeYear(e.target.value);
                setFilters((p) => ({ ...p, minYear: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Max"
              min={1800}
              max={CURRENT_YEAR}
              value={filters.maxYear}
              className={`bx-filter-input bx-filter-input-half${fieldErrors.foundedYear ? " vt-input-error" : ""}`}
              onChange={(e) => {
                const val = sanitizeYear(e.target.value);
                setFilters((p) => ({ ...p, maxYear: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <ValidationTooltip message={fieldErrors.foundedYear} />
        </div>

        {/* Sort By */}
        <div className="bx-filter-group">
          <label className="bx-filter-label">Sort By</label>
          <div className="bx-sort-wrap">
            <button
              type="button"
              className={`bx-sort-btn ${sortDropdownOpen ? "active" : ""}`}
              onClick={() => {
                setSortDropdownOpen(!sortDropdownOpen);

                setIndustryDropdownOpen(false);
                setBusinessTypeDropdownOpen(false);
                setCompanySizeDropdownOpen(false);
              }}
            >
              <span>
                {filters.sortBy === "newest"
                  ? "Newest First"
                  : filters.sortBy === "oldest"
                    ? "Oldest First"
                    : "Select sorting"}
              </span>

              <span className={`bx-sort-arrow ${sortDropdownOpen ? "open" : ""}`}>
                ▼
              </span>
            </button>

            {sortDropdownOpen && (
              <div className="bx-sort-dropdown">
                {[
                  { value: "newest", label: "Newest First" },
                  { value: "oldest", label: "Oldest First" },
                ].map((item) => (
                  <div
                    key={item.value}
                    className={`bx-sort-item ${filters.sortBy === item.value ? "selected" : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        sortBy: item.value,
                      }));

                      setSortDropdownOpen(false);
                    }}
                  >
                    <span>{item.label}</span>

                    {filters.sortBy === item.value && (
                      <span className="bx-sort-check">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bx-filter-actions">
          <Button block onClick={handleApplyFilters} className="bx-apply-btn">
            Apply Filters
          </Button>
          <Button
            block
            icon={<ReloadOutlined />}
            onClick={handleReset}
            className="bx-reset-btn"
          >
            Reset All
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <div className="bx-page">
      <div className="bx-container">
        {/* Hero */}
        <header className="bx-hero">
          <div className="bx-hero-content">
            <span className="bx-hero-tag">Real Estate Marketplace</span>
            <h1 className="bx-hero-title">Businesses</h1>
            <p className="bx-hero-desc">
              Discover innovative companies, startups, and enterprises across industries
            </p>
          </div>
          <div className="bx-hero-decoration" aria-hidden="true">
            <div className="bx-hero-circle bx-hero-circle--lg" />
            <div className="bx-hero-circle bx-hero-circle--sm" />
          </div>
        </header>

        {/* Search & Filter Bar */}
        <section className="bx-bar">
          <div className="bx-bar-top">
            <div className="bx-bar-count">
              <strong>{pagination.total.toLocaleString()}</strong> businesses available
            </div>
            {activeFiltersCount > 0 && (
              <button className="bx-clear-all" onClick={handleReset}>
                Clear all ({activeFiltersCount})
              </button>
            )}
          </div>

          <div className="bx-bar-controls">
            <div className="bx-search-field">
              <SearchOutlined className="bx-search-ico" />

              {/*
               * UPGRADED AutoComplete
               *
               * - onChange  → handleSearchChange (marks invalid, fetches suggestions)
               * - onSelect  → handleSearchSelect (marks valid, NO auto-fetch)
               * - value     → searchInputValue (controlled by hook)
               * - inner input onKeyDown → only fires handleSearchBarApply when valid
               */}
              <div
                className="bx-search-custom"
                ref={searchDropdownRef}
              >
                <input
                  type="text"
                  className="bx-search-input"
                  placeholder="Search by location, company name, or industry..."
                  maxLength={200}
                  value={searchInputValue}
                  onFocus={handleSearchFocus}
                  onChange={(e) => {
                    const value = e.target.value;

                    handleSearchChange(value);

                    setFilters((prev) => ({ 
                      ...prev,
                      search: value.slice(0, 200),
                      locality: "",
                      latitude: null,
                      longitude: null,
                      type: "",
                      industry: [],
                      companySize: "",
                      minYear: "",
                      maxYear: "",
                      sortBy: "",
                    }));

                    clearAllFieldErrors();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchBarApply();
                    }
                  }}
                />

                {searchSuggestions.length > 0 && (
                  <div className="bx-search-dropdown-custom">
                    {searchSuggestions.map((item, index) => (
                      <div
                        key={index}
                        className="bx-search-dropdown-item"
                        onClick={() => {
                          handleSearchSelect(item.value);
                          setSearchSuggestions([]);
                        }}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/*
             * UPGRADED Search button
             *
             * - disabled when no valid dropdown location has been selected
             */}
            <button
              className="bx-search-btn"
              onClick={handleSearchBarApply}
            >
              Search
            </button>

            <button
              className="bx-filter-btn"
              onClick={() => setFilterModalVisible(true)}
            >
              <FilterOutlined />
              <span className="bx-filter-btn-label">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="bx-filter-badge">{activeFiltersCount}</span>
              )}
            </button>
          </div>
        </section>

        {/* Modal Filter Panel */}
        <Modal
          title="Filter Businesses"
          open={filterModalVisible}
          onCancel={() => {
            setFilterModalVisible(false);
            clearAllFieldErrors();
          }}
          footer={null}
          centered
          width={modalWidth}
          className="bx-filter-modal"
          mask={{ closable: true }}
          styles={{
            body: {
              overflowY: "auto",
            },
          }}
        >
          {filterDrawer}
        </Modal>

        {/* Grid / States */}
        {loading ? (
          <div className="bx-state-wrap">
            <Spin size="large" />
            <p className="bx-state-msg">Loading businesses…</p>
          </div>
        ) : businesses.length === 0 ? (
          <div className="bx-state-wrap">
            <Empty description={<span className="bx-state-msg">No businesses found</span>} />
          </div>
        ) : (
          <>
            <Row gutter={[20, 20]} className="bx-grid">
              {businesses.map((item) => {
                const approvalStatus = item.approvalStatus || "pending";
                const activeStatus = item.status || "inactive";

                return (
                  <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={6} key={item._id}>
                    <article className="bx-card">
                      <div className="bx-card-thumb">
                        <img
                          alt={item.companyName}
                          src={
                            item.logo
                              ? item.logo + "?v=" + item.updatedAt
                              : item.images?.[0]
                                ? item.images[0] + "?v=" + item.updatedAt
                                : "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80"
                          }
                          className="bx-card-img"
                          loading="lazy"
                        />
                        <div className="bx-card-overlay" />
                        <div className="bx-card-tags">
                          {item.industry && (
                            <span className="bx-tag bx-tag--industry">{item.industry}</span>
                          )}
                          {item.type && (
                            <span className="bx-tag bx-tag--type">{item.type}</span>
                          )}
                        </div>
                        {item.isFeatured && (
                          <div className="bx-featured-badge">
                            <StarOutlined style={{ fontSize: "10px" }} />
                            <span>Featured</span>
                          </div>
                        )}
                      </div>

                      <div className="bx-card-body">
                        <div className="bx-card-location">
                          <EnvironmentOutlined className="bx-loc-ico" />
                          <span className="bx-loc-text">
                            {[
                              item.location?.locality,
                              item.location?.city,
                              item.location?.state,
                            ]
                              .filter(Boolean)
                              .map(
                                (part) =>
                                  part.charAt(0).toUpperCase() + part.slice(1)
                              )
                              .join(", ")}
                          </span>
                        </div>

                        <div className="bx-company-row">
                          <div className="bx-company-icon">
                            <BankOutlined style={{ fontSize: "13px" }} />
                          </div>
                          <h3 className="bx-card-name">{item.companyName}</h3>
                        </div>

                        {(item.tagline || item.description) && (
                          <p className="bx-tagline">
                            {item.tagline || item.description.substring(0, 80)}
                            {item.description?.length > 80 ? "…" : ""}
                          </p>
                        )}

                        <div className="bx-card-specs">
                          {item.companySize && (
                            <div className="bx-spec-item">
                              <span className="bx-spec-ico" aria-hidden="true">▪</span>
                              <span>{item.companySize} employees</span>
                            </div>
                          )}
                          {item.foundedYear && (
                            <div className="bx-spec-item">
                              <span className="bx-spec-ico" aria-hidden="true">⊞</span>
                              <span>Est. {item.foundedYear}</span>
                            </div>
                          )}
                        </div>

                        <div className="bx-card-footer">
                          {isAdmin && (
                            <div className="bx-card-statuses">
                              <span
                                className={`bx-pill bx-pill--${activeStatus === "active" ? "active" : "inactive"
                                  }`}
                              >
                                {activeStatus}
                              </span>
                              <span className={`bx-pill bx-pill--${approvalStatus}`}>
                                {approvalStatus}
                              </span>
                            </div>
                          )}
                          <Link to={`/business/${item.slug}`} className="bx-card-cta">
                            View details →
                          </Link>
                        </div>
                      </div>
                    </article>
                  </Col>
                );
              })}
            </Row>

            <div className="bx-pagination">
              <Pagination
                current={pagination.current}
                pageSize={15}
                total={pagination.total}
                onChange={handlePageChange}
                responsive
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Businesses;